import {useFormik} from 'formik';
import React, {useState} from 'react';
import {useNavigate} from 'react-router';
import {useQueryParams, StringParam} from 'use-query-params';
import * as yup from 'yup';

import {useSignupMutation} from '@/api';
import {TEL_PATTERN, TEL_MESSAGE} from '@/utils/tel';

import {PositiveButton} from '../../../components/mypage/MypageLayout';

export const validateSchema = yup.object().shape({
  email: yup.string().trim().required('メールアドレスは必須項目です'),
  lastName: yup.string().trim().required('姓は必須項目です'),
  firstName: yup.string().trim().required('名は必須項目です'),
  tel: yup
    .string()
    .trim()
    .required('TELは必須項目です')
    .matches(TEL_PATTERN, TEL_MESSAGE),
  password: yup
    .string()
    .trim()
    .min(6, 'パスワードは６文字以上で設定してください。')
    .required('パスワードを入力してください'),
  passwordConfirmation: yup
    .string()
    .trim()
    .required('パスワード(確認)を入力してください')
    .oneOf([yup.ref('password'), null], 'パスワードが一致していません'),
});

const SignupForm = () => {
  const [query] = useQueryParams({
    email: StringParam,
    firstName: StringParam,
    lastName: StringParam,
  });
  const {email = '', firstName = '', lastName = ''} = query;

  const [agree, setAgree] = useState(false);
  const navigate = useNavigate();
  const [signup, {data, loading}] = useSignupMutation({
    onCompleted: (data) => {
      if (!data?.signup?.error) {
        navigate('/signup/success');
      }
    },
  });

  const formik = useFormik({
    validationSchema: validateSchema,
    initialValues: {
      email,
      firstName,
      lastName,
      tel: '',
      nickname: '',
      password: '',
      passwordConfirmation: '',
    },
    onSubmit: (values) => {
      void signup({
        variables: {
          user: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            tel: values.tel,
            nickname: values.nickname,
          },
          password: {
            password: values.password,
            passwordConfirmation: values.passwordConfirmation,
          },
        },
      });
    },
  });

  return (
    <div className="mx-auto mb-[90px] w-[400px] pt-[90px] text-center">
      <h1 className="mb-[70px] text-center text-[26px] font-medium text-neutral-700">
        新規登録
      </h1>

      {data?.signup?.error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          <p>{data.signup.error}</p>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            姓<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="lastName"
            type="text"
            autoComplete="family-name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.lastName && formik.errors.lastName && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.lastName}
            </p>
          )}
        </div>

        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            名<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="firstName"
            type="text"
            autoComplete="given-name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.firstName && formik.errors.firstName && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.firstName}
            </p>
          )}
        </div>

        <div className="hidden">
          <label>Nickname</label>
          <input
            name="nickname"
            type="text"
            value={formik.values.nickname}
            onChange={formik.handleChange}
            autoComplete="off"
          />
        </div>

        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            TEL<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="tel"
            type="tel"
            autoComplete="tel"
            value={formik.values.tel}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.tel && formik.errors.tel && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.tel}
            </p>
          )}
        </div>

        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            メールアドレス<span className="ml-1 text-danger">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.email && formik.errors.email && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.email}
            </p>
          )}
        </div>

        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            パスワード<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="password"
            type="password"
            autoComplete="new-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.password && formik.errors.password && (
            <p className="mt-1 text-left text-sm text-red-600">
              {formik.errors.password}
            </p>
          )}
        </div>

        <div className="mb-[1em]">
          <label className="block text-left text-xs opacity-[0.37] [color:#333333]">
            パスワード(確認)<span className="ml-1 text-danger">*</span>
          </label>
          <input
            name="passwordConfirmation"
            type="password"
            autoComplete="new-password"
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="block h-[45px] w-full border border-neutral-100 bg-neutral-100"
          />
          {formik.touched.passwordConfirmation &&
            formik.errors.passwordConfirmation && (
              <p className="mt-1 text-left text-sm text-red-600">
                {formik.errors.passwordConfirmation}
              </p>
            )}
        </div>

        <div>
          <label className="block cursor-pointer text-left text-xs text-neutral-900">
            <input
              type="checkbox"
              onChange={(e) => {
                setAgree(e.target.checked);
              }}
              checked={agree}
              className="mr-[5px] mt-[3px]"
            />
            <a href="/terms" target="_blank">
              利用規約
            </a>
            と
            <a href="/privacy" target="_blank">
              プライバシーポリシー
            </a>
            に同意する
          </label>
        </div>

        <div className="my-[60px] flex justify-center">
          <PositiveButton
            type="submit"
            size="large"
            loading={loading}
            disabled={!agree || loading || !formik.isValid}>
            メールアドレスで登録
          </PositiveButton>
        </div>
      </form>
    </div>
  );
};

export {SignupForm};
